import { Injectable } from '@angular/core';

import { Observable, combineLatest, of, ReplaySubject, from, EMPTY, asyncScheduler } from 'rxjs';
import { take, map, distinctUntilChanged, shareReplay, first, catchError, switchMap, throttleTime } from 'rxjs/operators';

import { Mote, MoteState, MoteType, MoteSortType, MotesPaths, MediaEventMap, MoteEventType, MediaOptions, MoteWaypoint, Media, MoteData, MoteTranslation } from '../models';
import { Language, TranslationModel } from '../models';

import { Product } from './../models/product';
import { Comment } from '../models/comment';
import { Coords } from '../models/coords';
import { User } from '../models/user';

import { DatabaseService } from './database.service';
import { MediaService } from './media.service';
import { LanguageService } from './language.service';
import { UserStateService } from './user-state.service';

export interface NewStoryInfo {
    coords: Coords,
    addressInfo: AddressInfo,
    title: string,
    image: File,
    placeId?: string,
    placeIdName?: string,
    placeIdIsAttraction: boolean,
    placeIdCheckedOn?: number
}

export interface AddressInfo {
    name: string;
    address: string;
    city: string;
    country: string;
}

@Injectable({ providedIn: 'root' })
export class MoteService {

    public availableMotes: Array<Mote> = [];
    private availableMotesSubject: ReplaySubject<Mote[]> = new ReplaySubject<Mote[]>(1);

    public projectMoteReplayMap: { [projectKey: string]: Observable<Mote[]>; } = {};
    public childMoteReplayMap: { [tourKey: string]: Observable<Mote[]>; } = {};

    private initialized: boolean = false;

    constructor(
        private db: DatabaseService,
        private mediaService: MediaService,
        private userStateService: UserStateService,
    ) { }

    public init() {
        if(this.initialized)
            return;
        this.initialized = true;
    }

    public generateKey(): string {
        return Mote.genKey();
    }

    public async saveMote(mote: Mote): Promise<any> {
        // Get new id for mote
        if(!mote.key || mote.key.startsWith('new')) {
            mote.key = Mote.genKeyFromTitle(mote.title);
        }

        if(!mote.createdOn) {
            mote.createdOn = this.db.serverTime();
        }


        // Create database reference and update moteContent
        await this.db.database.ref(`motes/${mote.key}`).update(mote.toJson());

        // It's important that we update updatedOn AFTER saving actual changes to the mote
        // since the motes being saved might overwrite the new value otherwise
        await this.touchUpdatedOn(mote.key);
    }

    public async saveMoteProduct(moteKey: string, productKey: string, price: number): Promise<any> {
        await this.db.database.ref(`motes/${moteKey}`).update({ 'price': price, 'productLocked': true });
        await this.db.database.ref(`motes/${moteKey}/products/${productKey}`).set(true);
        await this.touchUpdatedOn(moteKey);
    }

    public async saveMoteProject(moteKey: string, projectKey: string, order: number): Promise<any> {
        await this.db.database.ref(`motes/${moteKey}`).update({ "project": projectKey, 'order': order });
        await this.touchUpdatedOn(moteKey);
    }


    public async saveMoteParent(moteKey: string, parentKey: string): Promise<any> {
        await this.db.database.ref(`motes/${moteKey}`).update({ "parent": parentKey });
        await this.touchUpdatedOn(moteKey);
    }

    public async setUpdatedOn(moteKey: string, updatedOn: number) {
        await this.db.database.ref(`motes/${moteKey}`).update({ 'updatedOn': updatedOn });
    }

    public async touchUpdatedOn(moteKey: string, updateParent: boolean = true) {

        await this.db.database.ref(`motes/${moteKey}`).update({ 'updatedOn': this.db.database.serverTime() });

        if(!updateParent) return;

        const databaseSnapshot = await this.db.database.ref(`motes/${moteKey}`).once('value');
        if(!databaseSnapshot.exists()) return;

        const mote = new Mote(moteKey, databaseSnapshot.val());

        if(!mote.parent) return;

        await this.touchUpdatedOn(mote.parent, false);
    }

    /**
     * Update the position of a `Mote`
     * @param moteKey key for the `Mote`
     * @param position a `Coords` object representing the position
     * @returns A `Promise` which resolves once the update on the server has completed
     */
    public async saveMotePosition(moteKey: string, position: Coords): Promise<any> {
        const newPosition = position ? position?.toJson() : null;
        await this.db.database.ref(`motes/${moteKey}`).update({ 'position': newPosition });
        await this.touchUpdatedOn(moteKey);
    }

    /**
     * Update the route segment of a `Mote`
     * @param moteKey key for the `Mote`
     * @param positions an array of position tuples
     * @returns A `Promise` which resolves once the update on the server has completed
     */
    public saveMoteRouteGeometry(moteKey: string, positions: [number, number][]): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ 'routeGeometry': positions });
    }

    public saveMoteLength(moteKey: string, length: number): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ "length": length });
    }

    public saveMoteDuration(moteKey: string, duration: number): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ "duration": duration });
    }

    public saveMoteStoryCount(moteKey: string, storyCount: number): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ "storyCount": storyCount });
    }

    public saveMoteStartPlace(moteKey: string, startPlace: string): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ "startPlace": startPlace });
    }

    public saveMoteEndPlace(moteKey: string, endPlace: string): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ "endPlace": endPlace });
    }

    public saveMoteCity(moteKey: string, city: string): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ "city": city });
    }

    public saveMoteWorldCityId(moteKey: string, cityId: string): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ "worldCityId": cityId });
    }

    public saveMoteCountry(moteKey: string, country: string): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ "country": country });
    }


    /**
     * Update the waypoints associated with a `Mote`
     * @param moteKey key for the `Mote`
     * @param waypoints an array of waypoints
     * @returns A `Promise` which resolves once the update on the server has completed
     */
    public saveMoteWaypoints(moteKey: string, waypoints: MoteWaypoint[]): Promise<any> {
        return this.db.database.ref(`motes/${moteKey}`).update({ 'waypoints': waypoints });
    }

    /**
     * Save mote order of multiple motes in one write operation
     * (and optionally mote route in the same operation)
     */
    public async saveMoteOrder(motes: Mote[], saveRoute: boolean = false): Promise<any> {
        if(!motes) return Promise.reject(new Error('No supplied Motes'));

        let updates = {};
        for(let mote of motes) {
            let key = mote.key || 'invalidMote';
            updates['motes/' + key + '/order'] = mote.order;
            updates['motes/' + key + '/parent'] = mote.parent;
            if(saveRoute) updates['motes/' + key + '/routeGeometry'] = mote.routeGeometry;
        }
        await this.db.database.ref().update(updates);

        const moteTouchUpdatePromises = motes.map((mote) => this.touchUpdatedOn(mote.key));
        await Promise.all(moteTouchUpdatePromises);
    }

    /**
     * Sets and overwrites mote MediaEvents and updates "UpdatedOn" for mote and parent
     */
    public async saveMoteEvents(moteKey: string, events: MediaEventMap) {
        await this.db.database.ref(`motes/${moteKey}`).update({ mediaEventMap: events });
        await this.touchUpdatedOn(moteKey);
    }

    /**
     * Updates mote content and updates "UpdatedOn" for mote and parent
     */
    public async updateMoteContent(moteKey: string, content: Partial<MoteData['content']>) {
        await this.db.database.ref(`motes/${moteKey}/content`).update(content);
        await this.touchUpdatedOn(moteKey);
    }

    /**
     * Save mote tags of multiple motes in one write operation
     */
    public async saveMoteTags(motes: Mote[]): Promise<any> {
        if(!motes) return Promise.reject(new Error('No supplied Motes'));

        let updates = {};
        for(let mote of motes) {
            let key = mote.key || 'invalidMote';
            updates['motes/' + key + '/tags'] = mote.tags;
        }
        await this.db.database.ref().update(updates);

        const moteTouchUpdatePromises = motes.map((mote) => this.touchUpdatedOn(mote.key));
        await Promise.all(moteTouchUpdatePromises);
    }

     /**
     * Mark a tour and all of its children as deleted (functions will clean up
     * database and delete media on scheduled time basis)
     */
    public async deleteTour(projectKey: string, tourKey: string): Promise<boolean> {
        if(!projectKey || !tourKey) return false;
        return this.deleteProjectMotes(projectKey, tourKey);
    }

    /**
     * Mark all motes in project to be deleted (functions will clean up
     * database and delete media on scheduled time basis)
     * @param projectKey Project key of the motes to be deleted
     * @param tourKey Optional tour key to only delete project motes of a specific tour
     */
    public async deleteProjectMotes(projectKey: string, tourKey?: string): Promise<boolean> {
        // Get all project motes
        const projectMotes = await this.watchProjectMotes([projectKey]).pipe(first()).toPromise();
        // Only delete tour project motes if tourKey is given as argument (filter out all tour motes and tour mote itself)
        const motes = tourKey ? projectMotes.filter(mote => (mote.parent == tourKey || mote.key == tourKey)) : projectMotes;
        // Create empty object to contain database changes
        let updates = {};

        for(let mote of motes) {
            // Make sure we never try ro delete non existing / inconsistent motes
            if(!mote.key) continue;
            // Update deleted on flag on mote
            updates[this.getMotePropertyPath(mote, 'deletedOn')] = this.db.serverTime();
            // Update publishedOn flag mote
            updates[this.getMotePropertyPath(mote, 'publishedOn')] = null;
        }
        // Apply the updates to all the motes at once
        return this.db.database.ref().update(updates).then( () => true ).catch( () => false );
    }

    private getMotePropertyPath(mote: Mote, property: keyof Mote) : string {
        return `motes/${mote.key}/${property}`;
    }

    public async deleteMote(moteKey: string): Promise<void> {
        // Get mote
        let mote = await this.getCachedMote(moteKey);
        if(!mote) return console.warn("Mote with key, " + moteKey + " does not exist, so cannot delete it");

        // Best-effort media link cleanup. A single link removal must never block
        // the deletion itself (e.g. orphaned media, or media owned by another
        // project/user that the current admin cannot write). Failures are logged
        // and the deletion proceeds.
        const mediaToUnlink = [
            ...(mote.mediaArray ?? []),
            ...(mote.sneakPeekMediaArray ?? []),
        ].filter(media => media?.key);

        await Promise.all(mediaToUnlink.map(media =>
            this.mediaService.deleteMediaLink(media.key, mote.key)
                .catch(err => console.warn(`Could not remove media link ${media.key} -> ${mote.key} while deleting mote (continuing):`, err))
        ));

        // Mark mote deleted (in cache)
        Mote.moteCache[moteKey] = null;
        // Delete mote for real if it is a tour (to free up the name space)
        if(mote.canHaveChildren()) {
            await this.db.database.ref(`motes/${moteKey}`).remove();
            // Tours will be marked deleted in Supabase in the tour-details-page by the deleteTour function 
        }
        else {
            // Mark story deleted
            const json = { "deletedOn": this.db.database.serverTime() };
            // Save mote
            await this.db.database.ref(`motes/${moteKey}`).update(json);
            // Do not mark the mote deleted in Supabase... until the creator publish the changes of the tour
        }

        // Get parent if any
        let parent = await this.getCachedMote(mote.parent);
        // Align mote order of children
        if(parent) {
            // Get all other children
            let children = (await this.getChildren(parent)).filter(mote => mote.key != moteKey);
            // Update order
            this.alignMotesOrder(children.sort((a, b) => a.order - b.order));
            await this.saveMoteOrder(children);
        }

        // Rename the mote
        //let releaseKey = mote.key;
        //mote.key = await this.restService.getUniqueKey("_" + mote.key).catch( err => "");
        // if(!mote.key) return console.warn("Could not move deleted mote, could not get key");
        // Save deleted mote in _ key namespace
        // await this.saveMote(mote);
        // Remove old key
        // return this.af.database.ref(`motes/${moteKey}`).remove();
    }

    public deleteMediaEvent(mote: Mote, mediaKeyToDelete: string) {
        if (!mote?.mediaEventMap) return;

        const eventArray = Object.entries(mote.mediaEventMap);
        const newEventMap = eventArray.filter(([_, value]) => {
            if(value.type != MoteEventType.SHOW_MOTE_MEDIA) return;
            const options = value.options as MediaOptions;
            return options.mediaKey != mediaKeyToDelete;
        });
        mote.mediaEventMap = Object.fromEntries(newEventMap);
    }

    private alignMotesOrder(motes: Mote[]) {
        motes.forEach((mote, idx) => {
            mote.order = idx + 1;
        });
        return motes;
    }

    public unDeleteMote(moteKey: string) {
        let json = { "deleted": false };
        this.db.database.ref(`motes/${moteKey}`).update(json);
    }

    public deleteStats() {
        console.log("Delete stats now: ");
        this.db.database.ref("/sessions").remove();
    }

    public checkIfMoteExists(moteKey): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const ref = this.db.database.ref('motes/' + moteKey);
            ref.once('value').then((ev) => {
                let bool: boolean = ev.exists();
                resolve(bool);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    /**
     * Watch motes of projects (this will return a shared "cached" replay subject if already requested once by the user)
     */
    public watchProjectMotes(enabledProjects: Array<string> = [], showDeleted: boolean = false): Observable<Mote[]> {
        if(!enabledProjects || (enabledProjects.length >= 1 && enabledProjects[0] == null)) {
            console.log("Invalid project / null array detected", enabledProjects);
            return from([]);
        }

        // Find the cache key of this project mote request
        let cacheKey = enabledProjects.join("_") + "_" + showDeleted;
        if(this.projectMoteReplayMap[cacheKey]) {
            // Return cached observable
            return this.projectMoteReplayMap[cacheKey];
        }

        // Get the project mote
        // Pipe through shareReplay without refcount as we never want to automatically unsubscribe - we want the motes cached for good
        let observable = this.watchProjectMotesFb(enabledProjects, showDeleted).pipe(shareReplay(1));

        // Cache replay subject for other observers
        this.projectMoteReplayMap[cacheKey] = observable;
        // Return observable
        return observable;
    }

    /**
     * Watch all published tours (cached)
     */
    public watchPublishedTourMotes(): Observable<Mote[]> {
        const cacheKey = "PublishedTourMotesCache";
        // Get cached tour reply subject if already requested once
        if(this.projectMoteReplayMap[cacheKey]) return this.projectMoteReplayMap[cacheKey];

        // Create sorted and cached motes observer
        const sortMotes = map((motes: Mote[]) => motes.sort( (a, b) => this.sortByOrderAndTimestamp(a, b)));
        const tourQuery = query => query.orderByChild('publishedOn').startAt(1);

        // Pipe through shareReplay without refcount as we never want to automatically unsubscribe - we want the motes cached for good
        let observable = this.db.list("/motes", tourQuery).snapshotChanges().pipe(map(json => Mote.fromJsonList(json)), sortMotes).pipe(shareReplay(1));

        // Cache replay subject for other observers
        this.projectMoteReplayMap[cacheKey] = observable;
        // Return observable
        return observable;
    }

    private sortByOrderAndTimestamp(a: Mote, b: Mote): number {
        // First, compare by the order value
        if (a.order < b.order) return -1;
        if (a.order > b.order) return 1;
        return a.title.localeCompare(b.title);
    }

    /**
     * Get motes of specific language or fall back to original language (en)
     */
    public languageFilter(motes: Mote[], language: string): Mote[] {
        if(!language) return motes;

        // console.log("Apply language filter ", motes, language);
        const OriginalLanguage = Language.en;
        // Find all original (english) motes
        let originalMotes = motes.filter(mote => mote.language == OriginalLanguage || !mote.originKey);

        // Swap original with translation if it exists
        originalMotes.forEach((originMote, idx) => {
            // Find the preferred translation
            let translation = motes.find(mote => originMote.key == mote.originKey && mote.language == language);
            // Swap mote with the translation
            if(translation) originalMotes[idx] = translation;
        });
        // Return translated list
        return originalMotes;
    }

    public async setLanguage(moteKey: string, language: Language): Promise<any> {
        await  this.db.object(MotesPaths.MOTE_DB_PATH(moteKey + "/language")).set(language);
        await this.touchUpdatedOn(moteKey);
    }

    /**
     * Set the project key / world key of a mote (transfer mote to another project)
     */
    public async setProject(moteKey: string, projectKey: string): Promise<any> {
        await this.db.object(MotesPaths.MOTE_DB_PATH(moteKey + "/project")).set(projectKey);
        await this.touchUpdatedOn(moteKey);
    }

    public async setOrigin(moteKey: string, originKey: string): Promise<any> {
        console.log("Mote key left, right", moteKey, originKey);
        await this.db.object(MotesPaths.MOTE_DB_PATH(moteKey + "/originKey")).set(originKey);
        await this.touchUpdatedOn(moteKey);
    }

     /**
     * Save origin or multiple motes to database in one operation
     * @param motes Motes with origins to be saved in the database
     */
    public async saveOrigins(motes: Mote[]): Promise<boolean> {
        // Create empty object to contain database changes
        let updates = {};

        for(let mote of motes) {
            // Don't try to update non existing / inconsistent motes
            if(!mote.key) continue;
            // Update origin of mote
            updates[this.getMotePropertyPath(mote, 'originKey')] = mote.originKey;
            // Update updated on flag
            updates[this.getMotePropertyPath(mote, 'updatedOn')] = this.db.database.serverTime();
        }
        // Apply the updates to all the motes at once
        return this.db.database.ref().update(updates).then( () => true ).catch( () => false );
    }

    /**
     * Set the type of a `Mote` in the database
     * @param moteKey Key for the `Mote`
     * @param type The new `MoteType`
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public async setType(moteKey: string, type: MoteType, sortBy: MoteSortType = null): Promise<any> {
        await  this.db.object(MotesPaths.MOTE_DB_PATH(moteKey + "/type")).set(type);
        // Change sort by according to mote type
        if(!sortBy) sortBy = (type == MoteType.COLLECTION) ? MoteSortType.DISTANCE : MoteSortType.ORDER;
        await this.db.object(MotesPaths.MOTE_DB_PATH(moteKey + "/sortBy")).set(sortBy);

        await this.touchUpdatedOn(moteKey);
    }

    public languageFilterStrict(motes: Mote[], language: string): Mote[] {
        // Find all motes with the give language
        return motes.filter(mote => mote.language == language);
    }

    public async getTranslation(mote: Mote, language: string): Promise<Mote> {
        // If no mote given, indicate that no translation exist by returning null
        if(!mote) {
            return null;
        }

        let projectMotes = await this.watchProjectMotes([mote.project]).pipe(first()).toPromise();

        let translation = projectMotes.find(projectMote => ((projectMote.key == mote.originKey || projectMote.originKey == mote.key) && projectMote.language == language));
        return translation || null;
    }

   /**
     * Get mote translations
     * @param mote Any type of mote
     * @returns All (published) motes (key and lang) that are language versions of the given mote
     */
    public watchTranslations(mote: Mote): Observable<MoteTranslation[]> {
        return this.getTranslationModel(mote).pipe(map((model) => {
            const originKey = mote.originKey || mote.key;
            return model.translationMap[originKey] || [];
        }))
    }

    /**
     * Get mote translation model (used for resolving translations)
     * @param mote Any type of mote
     * @param onlyPublished Set true to only get translations that are published
     * @returns All (allowed) motes (key and lang) that are language versions of the given mote (as a database model)
     */
    public getTranslationModel(mote: Mote, onlyPublished: boolean = false): Observable<TranslationModel> {
        // Watch origin mote:
        // If mote has originKey, then it is a translation of origin mote.
        // If it does not, then mote is the origin mote.
        // User might not have the rights to watch another origin mote, and so an error must be caught.
        // We do not terminate on permission error, because we still wish to watch the remaining translations.
        const originMoteObs = mote.originKey ? this.watchMote(mote.originKey).pipe(catchError((err) => of({}))) : of(mote);
        // Watch all translation motes:
        // User always has permission to watch motes via originKey.
        const originKey = mote.originKey || mote.key;
        const transObs = this.getMotesByOrigin(originKey);

        let motes: Mote[] =  [];
        return combineLatest([originMoteObs, transObs]).pipe(switchMap(([origin, translations]) => {
            // Filter out unpublished translations if applicable
            if(onlyPublished) translations = translations.filter(mote => mote.publishedOn);
            // Combine origin mote (if not empty due to permission error) and translations
            motes = (origin instanceof Mote) ? [origin].concat(translations) : translations;
            // Make sure they belong to the same project
            motes = motes.filter((langMote) => langMote.project == mote.project);
            // For stories, attempt to fetch all their parents (if input mote is a story then so are all its translations)
            if(mote.parent) {
                const parentKeyObs = motes.map((story) => this.watchMote(story.parent).pipe(map((parent) => parent.key), catchError((err) => of(''))));
                return combineLatest(parentKeyObs);
            }
            return of([]);
        }), map((parentKeys: string[]) => {
            // Filter out all stories belonging to tours where permission was denied
            if(mote.parent) {
                motes = motes.filter((story) => parentKeys.includes(story.parent));
            }
            // Create translation map
            let translationModel = new TranslationModel();
            translationModel.translationMap = LanguageService.getSimpleTranslationMap(motes);
            return translationModel;
        }))
    }

    private getMotesByOrigin(originKey: string): Observable<Mote[]> {
        const query = (ref) => ref.orderByChild('originKey').equalTo(originKey);

        return this.db.list(`/motes`, query).snapshotChanges().pipe(map(snapList => {
            if(!snapList) return null;
            return Mote.fromJsonList(snapList);
        }));
    }

    /**
     * Get map translations related to an array of motes (keys: languages, values: mote IDs)
     */
    public getTranslationModels(motes: Mote[]): Observable<TranslationModel[]> {
        if(!motes || motes.length <= 0) return of([]);

        let observables = motes.map(mote => this.getTranslationModel(mote));
        // Merge all query observable outputs
        return combineLatest(...observables);
    }

    /**
     * Watch motes of projects (this is the bare FB accessor without RXJS caching)
     */
    private watchProjectMotesFb(enabledProjects: Array<string> = [], showDeleted: boolean = false): Observable<Mote[]> {

        const sortMotes = map((motes: Mote[]) => MoteService.sortMotes(motes, MoteSortType.ORDER));

        // If no specific projects enabled
        if(enabledProjects.length <= 0 || enabledProjects[0] == "*") {
            throw "Deprecated, can not get all stories in the world at the same time...why?";
        }

        // Generate list of observable with mote project filter query
        let observables = enabledProjects.map((project) => {
            return this.db.list("/motes", query => query.orderByChild('project').equalTo(project)).snapshotChanges();
        });

        // Merge all query observable outputs
        let motes = combineLatest(...observables).pipe(map((json) => {
            // Merge array of array
            let combined = [].concat(...json);
            return Mote.fromJsonList(combined, showDeleted);
        }));

        return motes.pipe(sortMotes);
    }

    /**
    * Obsoleted TODO: Delete?
    * @param enabledProjects
    */
    public watchAllMotes(enabledProjects: Array<string> = []): Observable<Mote[]> {
        return this.watchProjectMotes(enabledProjects);
        //return this.watchAllSubject;
    }

    /**
     * Watch routes and collections of a project
     * @param projectKey
     */
    public watchCollectionMotes(projectKey: string, languageObserver: Observable<Language> = null): Observable<Mote[]> {
        let collectionObs = this.watchProjectMotes([projectKey]).pipe(map(motes => {
            return motes.filter(mote => (mote.canHaveChildren()));
        }));

        let languageFiltered = combineLatest(collectionObs, languageObserver).pipe(map(([motes, language]) => this.languageFilter(motes, language)));
        // Subscribe to collections (motes) of this world
        return languageObserver ? languageFiltered : collectionObs;
    }

    /**
     * Watch only published routes and collections of a project
     * @param projectKey
     * @param languageObserver Optional language observer if tour list should be language specific
     */
    public watchPublishedTours(projectKey: string, languageObserver: Observable<Language> = null): Observable<Mote[]> {
        // Watch all published tours and filter out all from the given project
        let tourObs = this.watchPublishedTourMotes().pipe(map(motes => motes.filter(mote => mote.project == projectKey)));
        // Filter out tours with other languages than the preferred
        let languageFiltered = combineLatest(tourObs, languageObserver).pipe(map(([tours, language]) => this.languageFilter(tours, language)));
        // Return observer of all tours or language filtered tours
        return languageObserver ? languageFiltered : tourObs;
    }

    /**
     * Watch project tours and sort by updatedOn
     * @param projectKey
     */
    public watchLatestTours(projectKey: string): Observable<Mote[]> {
        // Function to extract tours and sort by 'updatedOn'
        const filterMotes = map((motes: Mote[]) => motes.filter(mote => mote.canHaveChildren()));
        const sortMotes = map((motes: Mote[]) => MoteService.sortMotes(motes, MoteSortType.UPDATED));

        // Fetch project motes
        const tourQuery = query => query.orderByChild('project').equalTo(projectKey);
        let observable = this.db.list("/motes", tourQuery).snapshotChanges().pipe(map(json => Mote.fromJsonList(json)), filterMotes, sortMotes);

        return observable;
    }

    /**
    * Watch a mote and get live updates.
    * Throws error if user does not have permission to read mote (tour is unpublished and user is not creator nor a project member).
    */
    public watchMote(moteKey: string, useCache: boolean = false): Observable<Mote> {
        if(!moteKey) {
            return of(null);
        }

        // Return immediately if mote is already in cache (turbo turbo)
        // OBS: Only use the cache in the app as it might have impact on Creator upload (StoryHunt/creator#23)
        if(useCache && Mote.moteCache[moteKey]) return of(Mote.moteCache[moteKey]);

        // Get mote from DB and map to Mote object
        return this.db.object('motes/' + moteKey).snapshotChanges().pipe(map(json => Mote.fromJson(json)));
    }

    /**
     * Get a mote once as promise.
     * Catch error on permission denied and return null.
     */
    public getMote(moteKey: string, useCache: boolean = false): Promise<Mote> {
        return this.watchMote(moteKey, useCache).pipe(take(1), catchError((err) => of(null))).toPromise();
    }

    /**
     * Watch first mote instance matching the given previewKey,
     * or watch mote by moteKey if no previewKey is given.
     * Throws error if user does not have permission to read mote (tour is unpublished and user is not creator nor a project member).
     */
    public watchMotePreview(moteKey: string, previewKey?: string): Observable<Mote> {
        if(!previewKey) return this.watchMote(moteKey, true);

        let query = (ref) => ref.orderByChild('previewKey').equalTo(previewKey);

        return this.db.list(`/motes`, query).snapshotChanges().pipe(map(snapList => {
            if(!snapList) return null;
            let motes = snapList.map((snap) => Mote.fromJson(snap));
            return motes.length > 0 ? motes[0] : null;
        }));
    }

    /**
     * Get first mote instance matching the given previewKey.
     * Catch error on permission denied and return null.
     */
    public getMotePreview(moteKey: string, previewKey?: string): Promise<Mote> {
        return this.watchMotePreview(moteKey, previewKey).pipe(take(1), catchError((err) => of(null))).toPromise();
    }

    /**
    * Watch a list of motes
    */
    public watchMotes(moteKeys: string[]): Observable<Mote[]> {
        if(!moteKeys || moteKeys.length <= 0) return of([]);

        let observables = moteKeys.map(key => this.watchMote(key));
        // Merge all query observable outputs
        return combineLatest(...observables);
    }

    /**
     * Watch available motes within users range to be unlocked
     */
    public watchAvailableMotes(): Observable<Mote[]> {
        // Function to create unique hash of mote keys ignoring order
        let arrayHash = (motes: Mote[]) => motes.map(mote => mote.key).sort().join('|');
        // Compare mote lists to see if it changed
        let compareLists = (oldList: Mote[], newList: Mote[]) => arrayHash(oldList) == arrayHash(newList);
        // Return distinct list of available motes
        return this.availableMotesSubject.pipe(distinctUntilChanged(compareLists));
    }

    public watchMotesByUser(uid: string): Observable<Mote[]> {
        // Create new query for motes by user
        let motesByUser = this.db.list("/motes",
            ref => ref.orderByChild('createdBy').equalTo(uid))
            .snapshotChanges().pipe(map(json => Mote.fromJsonList(json)));

        return motesByUser;
    }

    public watchMotesByDate(date: string | number, before: number = 0.5, after: number = 0.5): Observable<Mote[]> {

        let dateAsNumber: number = Number(date);
        let dayStart = new Date(dateAsNumber).setHours(0, 0, 0, 0);
        let dayEnd = new Date(dateAsNumber).setHours(23, 59, 0, 0);

        // Create new query for motes based on given date and days to query for
        let motesByUser = this.db.list("/motes",
            ref => ref.orderByChild('createdOn').startAt(dayStart).endAt(dayEnd))
            .snapshotChanges().pipe(map(json => Mote.fromJsonList(json)));

        return motesByUser;
    }

    public getCachedMote(moteKey: string): Promise<Mote> {
        // Get mote from cache
        if(Mote.moteCache[moteKey]) return Promise.resolve(Mote.moteCache[moteKey]);
        // Fall back to mote from database
        return this.getMote(moteKey);
    }

    /**
     * Save a `Mote` in the cache.
     * If a `Mote` with the same key exists in the cache, it will be overwritten.
     * @param mote The `Mote` which should be cached
     */
    public setCachedMote(mote: Mote): void {
        Mote.moteCache[mote.key] = mote;
    }

    /**
     * Watch all tour children
     * @param parent Parent / tour mote
     */
    public watchChildren(parent: Mote, enableCache: boolean = false): Observable<Mote[]> {
        if(!parent) return EMPTY;

        let query = (ref) => ref.orderByChild('parent').equalTo(parent.key);

        if(enableCache && this.childMoteReplayMap[parent.key]) {
            // Return cached observable
            return this.childMoteReplayMap[parent.key];
        }

        const moteChildren = this.db.list(`/motes`, query).snapshotChanges().pipe(map(snapList => {
            if(!snapList) return [];
            const children = snapList.map((snap) => Mote.fromJson(snap));
            return children.filter((child) => child && !child.deletedOn && (child.project == parent.project));
        })).pipe(shareReplay(1));
        // Save mote children in cache
        this.childMoteReplayMap[parent.key] = moteChildren;

        return moteChildren;
    }

    /**
     * Get all tour children
     * @param parent Parent / tour mote
     */
    public getChildren(parent: Mote): Promise<Mote[]> {
        return this.watchChildren(parent, true).pipe(first()).toPromise();
    }

    /**
     * Fetch children by parent key (e.g. for reparenting when parent has broken key)
     * @param parentKey Parent mote key
     * @param projectKey Optional: filter children to same project
     */
    public getChildrenByParentKey(parentKey: string): Promise<Mote[]> {
        if(!parentKey) return Promise.resolve([]);
        const query = (ref) => ref.orderByChild('parent').equalTo(parentKey);
        return this.db.list(`/motes`, query).snapshotChanges().pipe(
            first(),
            map(snapList => {
                if(!snapList) return [];
                const children = Mote.fromJsonList(snapList, true);
                return (children || []).filter((child) => child && !child.deletedOn);
            })
        ).toPromise();
    }

    public addComment(moteKey: string, comment: Comment) {
        if(!comment.createdOn) comment.createdOn = this.db.serverTime();

        this.db.list("motes/" + moteKey + "/comments/").push(comment.toJson());
    }

    public removeComment(moteKey: string, comment: Comment): boolean {
        this.db.object("motes/" + moteKey + "/comments/" + comment.key).remove();
        return true;
    }

    public updateMoteDistances(motes: Mote[], userPos: Coords) {
        if(!userPos || !userPos.isValid || (userPos.latLong()[0] == 0 && userPos.latLong()[1] == 0)) return;

        // Update distance in all motes
        for(let mote of motes) {
            mote.updateDistance(userPos);
        }
    }

    public updateMoteStates(motes: Mote[], user: User, activeRouteKey = "", exclusiveRouteMode: boolean = false, emitMoteUpdates: boolean = false): void {
        let userRadius = user ? user.userRadius : User.INIT_USER_RADIUS;
        // Update all moteStates
        this.availableMotes = [];
        for(let mote of motes) {
            let state = mote.updateState(user, userRadius, activeRouteKey, exclusiveRouteMode);
            // Emit change detection events to local views
            if(emitMoteUpdates) mote.emitUpdates();
            // Only notify about available motes of the active route (no translations etc.)
            if(state == MoteState.AVAILABLE && mote.parent == activeRouteKey) this.availableMotes.push(mote);
        }
        // Notify listeners about available motes
        this.availableMotesSubject.next(this.availableMotes);
    }

    public watchProducts(projectKey: string): Observable<Product[]> {
        return this.db.object('products/' + projectKey).snapshotChanges().pipe(map((snap) => {
            let jsonMap = snap.payload.val();
            return Object.keys(jsonMap).map((key) => new Product(key, jsonMap[key]));
        }));
    }

    public saveProducts(product: Product) {
        if(!product.project) return console.error("Can't save product, no project key!");
        // Create new product if it dosent exist
        if(!product.key) {
            product.key = this.generateKey();
            product.createdOn = this.db.serverTime();
        }
        return this.db.database.ref(`products/${product.project}/${product.key}`).update(product.toJson());
    }

    /**
     * Sort mote array by the given MoteSortType
     */
    public static sortMotes(motes: Mote[], sortBy: MoteSortType): Mote[] {
        switch(sortBy) {
            case MoteSortType.NO_SORTING:
                return motes;

            case MoteSortType.DISTANCE:
                return motes.sort((a, b) => a.distance - b.distance);

            case MoteSortType.TITLE:
                return motes.sort((a, b) => a.title.localeCompare(b.title));

            case MoteSortType.KEY:
                return motes.sort((a, b) => a.key.localeCompare(b.key));

            case MoteSortType.START_DATE:
                return motes.sort((a, b) => (a.startTime && b.startTime) ? a.startTime.getTime() - b.startTime.getTime() : -1);

            case MoteSortType.UPDATED:
                return motes.sort((a, b) => (a.updatedOn && b.updatedOn) ? a.updatedOn.getTime() - b.updatedOn.getTime() : -1);

            case MoteSortType.ORDER:
            default:
                return motes.sort((a, b) => a.order - b.order);
        }
    }

    /**
     * Find the point in route geometry closest to a mote and return index of the point
     */
    private findClosestRoutePoint(mote: Mote, routePoints: [long: number, lat: number][]): number {
        if(!mote || !routePoints) {
            console.warn("Could not find closest route point of [mote, routePoints]", mote, routePoints);
            return;
        }
        // Map distance from mote to each point on the route
        let distances = routePoints.map((point, idx) => {
            let distance = Coords.calcDistance(new Coords(point[1], point[0]), mote.position);
            // let distance = mote.calcDistance(point[1],point[0], mote.position.lat, mote.position.long);
            return { distance, idx };
        });
        // Sort to find the shortest distance
        distances.sort((a, b) => (a.distance - b.distance));
        // Return index of the shortest distance
        return (distances.length > 0) ? distances[0].idx : -1;
    }

    /**
     * Automatically update the order of the motes in a route or schedule
     * @param mote A mote which is either a route, part of a route, or part of a schedule
     */
    public async autoUpdateMoteOrder(mote: Mote): Promise<void> {
        // Get parent route of the mote
        let parent = (mote.parent) ? await this.getCachedMote(mote.parent) : null;
        let hasRouteParent = (parent && (parent.type == MoteType.ROUTE || parent.type == MoteType.QUEST));
        let hasScheduleParent = (parent && (parent.type == MoteType.SCHEDULE));
        let isRoute = (mote.type == MoteType.ROUTE || mote.type == MoteType.QUEST);

        // Update order of all motes in of parent route when position has changed on mote
        if(hasRouteParent) {
            // Get all children
            let children = await this.getChildren(parent);
            // Auto update mote order and mote routes
            await this.autoUpdateMoteRouteOrder(children, parent);
        }

        // Update order of all child motes when parent route geometry has changed
        if(isRoute) {
            let children = await this.getChildren(mote);
            // Auto update mote order and mote routes
            await this.autoUpdateMoteRouteOrder(children, mote);
        }

        if(hasScheduleParent) {
            let children = await this.getChildren(parent);
            // Auto update mote order and mote routes
            await this.autoUpdateMoteScheduleOrder(children, parent);
        }
    }

    /**
     * Automatically update the `order` and `routeGeometry` properties of motes in a tour with type `ROUTE` or `QUEST`.
     * The results are based on the projection of the mote position on the route geometry of the tour.
     * The `routeGeometry` property of a child `Mote` should be an array of positions leading from the previous `Mote` to the one in question.
     * @param motes The child motes of a tour
     * @param route The parent `Mote` of type `ROUTE` or `QUEST`
     * @deprecated This function should only be used for converting motes in tours made in StoryHunt Studio to the format used in StoryHunt Creator
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    public autoUpdateMoteRouteOrder(motes: Mote[], route: Mote): Promise<any> {
        // Only auto update Quest and Route Mote container types
        if(route.type != MoteType.QUEST && route.type != MoteType.ROUTE) return;

        // Filter out tours if any
        motes = motes.filter(mote => (mote.type == MoteType.PLACE || mote.type == MoteType.EVENT));

        // Find distance to the closest point on route
        let motePointMap = motes.map(mote => {
            let pointIdx = this.findClosestRoutePoint(mote, route.getRoute());
            return { pointIdx, mote };
        });

        // Sort by index of route point
        motePointMap = motePointMap.sort((a, b) => (a.pointIdx - b.pointIdx));

        // Update the order with the index of lowest to highest route point index
        motePointMap.forEach((motePoint, idx) => {
            // Set the order of the mote that fits in the order on the route
            motePoint.mote.order = idx + 1;
            // Find the previous mote point on route
            let prevPoint = (idx <= 0) ? null : motePointMap[idx - 1];
            // Get the route geometry
            let routeGeometry = route.getRoute();

            // Find the part of the route that belongs to this mote (the route leading to up to the mote)
            motePoint.mote.routeGeometry = (prevPoint) ? routeGeometry.slice(prevPoint.pointIdx, motePoint.pointIdx + 1) : [];
        });

        // Save the new motes order
        return this.saveMoteOrder(motes, true);
    }


    /**
     * Automatically update the order of the motes in a schedule based on the
     * the start time of the motes
     */
    public autoUpdateMoteScheduleOrder(motes: Mote[], tour: Mote): Promise<any> {
        // Only auto update Schedule container types
        if(tour.type != MoteType.SCHEDULE) return;
        // Filter out tours if any
        motes = motes.filter(mote => (!mote.canHaveChildren()));
        // Sort by index of route point
        motes.sort((a, b) => (a.startTime.getTime() - b.startTime.getTime()));
        // Update the order with the index of lowest to highest route point index
        motes.forEach((mote, idx) => {
            // Set the order of the mote that fits in the order on the route
            mote.order = idx + 1;
        });
        // Save the new motes order
        return this.saveMoteOrder(motes, false);
    }

    /**
     * Automatically update the position of a tour. The position is calculated based on tour type and contained `Mote` objects
     * @param mote A `Mote` of any type (i.e. if a `Mote` contained within a tour is passed, it's parent's position will be updated)
     * @returns A `Promise` which resolves once the update on the server has completed
     */
    public async autoUpdateTourPosition(mote: Mote): Promise<any> {
        const tourTypes = [
            MoteType.COLLECTION,
            MoteType.QUEST,
            MoteType.ROUTE,
            MoteType.SCHEDULE,
        ];
        // Get the tour Mote
        const tourMote = tourTypes.includes(mote.type) ? mote : await this.getCachedMote(mote.parent);
        // Get all children
        const children = await this.getChildren(tourMote);

        // If the tour has no child Motes, do not update the tour position
        if(children.length == 0) return null;

        // If the tour Mote is of type `ROUTE` or `QUEST`, the tour position is the position of the first Mote (i.e. the Mote with the lowest `order` value)
        if(tourMote.type == MoteType.ROUTE || tourMote.type == MoteType.QUEST) {
            const firstMote = MoteService.sortMotes(children, MoteSortType.ORDER)[0];

            return this.saveMotePosition(tourMote.key, firstMote.position);
        }

        // If the tour is of any other type, the tour position is the average of all contained positions
        let coords = children.map(mote => mote.position);
        let averageCoords = Coords.calcAverageCoords(coords);

        return this.saveMotePosition(tourMote.key, averageCoords);
    }

    public getServerTimestamp(): any {
        return this.db.serverTime();
    }

    /**
     * Delete all media events of type SHOW_MEDIA_EVENT from a given mote
     */
    public async deleteShowMediaEvents(mote: Mote): Promise<void> {
        const eventFilteredEntries = Object.entries(mote.mediaEventMap || {}).filter(([_, event]) => event.type != MoteEventType.SHOW_MOTE_MEDIA);
        await this.saveMoteEvents(mote.key, Object.fromEntries(eventFilteredEntries));
        await this.touchUpdatedOn(mote.key);
    }

    /**
     * Create a story within a tour
     */
    public async createStory(title: string, addressInfo: AddressInfo, coords: Coords, tour: Mote, existingStories: Mote[], userKey: string, placeId?: string, placeIdName?: string, placeIdIsAttraction?: boolean, placeIdCheckedOn?: number): Promise<Mote> {
        const newStoryKey = this.generateKey();
        const newStory = new Mote(newStoryKey);

        newStory.title = title;
        newStory.address = addressInfo.address;
        newStory.city = addressInfo.city;
        newStory.country = addressInfo.country;
        newStory.setPosition(coords);
        newStory.createdBy = userKey;
        newStory.placeId = placeId;
        newStory.placeIdName = placeIdName;
        newStory.placeIdIsAttraction = placeIdIsAttraction;
        newStory.placeIdCheckedOn = placeIdCheckedOn;
        
        this.setInheritedValues(newStory, tour, existingStories.length);
        this.setTypeValues(newStory);

        const mainMedia = new Media();
        mainMedia.project = newStory.project;

        mainMedia.createdBy = userKey;
        mainMedia.createdOn = this.db.database.serverTime();
        mainMedia.order = 0;

        newStory.mediaArray = [mainMedia];
        newStory.image = mainMedia;

        await this.saveMote(newStory);
        return newStory;
    }

    private setInheritedValues(story: Mote, tour: Mote, numberOfStories: number, type: MoteType = MoteType.PLACE) {
        story.parent = tour.key;
        story.project = tour.project;
        story.type = type;
        story.language = tour.language;
        story.order = numberOfStories + 1;
    }

    private setTypeValues(story: Mote) {
        switch(story.type) {
            case MoteType.EVENT:
                story.defaultState = MoteState.UNLOCKED;
                break;
            case MoteType.SCHEDULE:
                story.defaultState = MoteState.UNLOCKED;
                story.sortBy = MoteSortType.START_DATE;
                story.publishedOn = null;
                break;
            case MoteType.ROUTE:
                story.defaultState = MoteState.UNLOCKED;
                story.sortBy = MoteSortType.ORDER;
                story.publishedOn = null;
                break;
            case MoteType.COLLECTION:
                story.defaultState = MoteState.UNLOCKED;
                story.sortBy = MoteSortType.DISTANCE;
                story.publishedOn = null;
                break;
        }
    }

    /**
     * Take a list of motes and filter out translations
     * @return A list of "original" motes
     * @deprecated Use LanguageService.getSimpleTranslationMap or similar instead
     */
    public translationFilter(mote: Mote, motes: Mote[]): Mote[] {
        return motes.filter(projectMote => (mote.key != projectMote.key) && ((mote.originKey != "" && mote.originKey == projectMote.originKey) || mote.originKey == projectMote.key || projectMote.originKey == mote.key));
    }

    /**
     * Use stored previewKey in order to access tour mote, which might not be published nor owned by user.
     * If previewKey does not match the requested tour, try to access it without.
     * Otherwise return null (permission denied).
     * @param tourKey Key of the tour mote to fetch
     * @returns
     */
     public getTour(tourKey: string): Promise<Mote> {
        if(!tourKey) return null;
        const activeTour = this.userStateService.getStoredActiveTour();
        const previewKey = (tourKey == activeTour.tourKey) ? activeTour.previewKey : '';
        return this.getMotePreview(tourKey, previewKey);
    }

    /**
     * Use stored previewKey in order to access tour mote, which might not be published nor owned by user.
     * If previewKey does not match the requested tour, try to access it without.
     * If permission is denied, a firebase error will be thrown.
     * @param tourKey Key of the tour mote to watch
     * @returns
     */
    public watchTour(tourKey: string): Observable<Mote> {
        if(!tourKey) of(null);
        const activeTour = this.userStateService.getStoredActiveTour();
        const previewKey = (tourKey == activeTour.tourKey) ? activeTour.previewKey : '';
        return this.watchMotePreview(tourKey, previewKey);
    }
}
